import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap"
import { Form } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router-dom"
import { UncontrolledTooltip } from "reactstrap"
import toastr from "toastr"
import TableContainer from "../../components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import apiURl from "url"

const Companies = () => {
  // Meta title
  document.title = "Companies"

  const navigate = useNavigate()

  // State management
  const [showPassword, setShowPassword] = useState(false)
  const [companies, setCompanies] = useState([])
  const [accountModal, setAccountModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteID, setDeleteID] = useState(null)
  const [editCompany, setEditCompany] = useState(null)
  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    phone: "",
    password: "",
    contactPerson: "",
    businessType: "Retailer",
    address: "",
    taxId: "",
    registrationCertificate: null,
    companyLogo: null,
    website: "",
  })

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleFileChange = e => {
    const { name } = e.target
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({ ...prev, [name]: file }))
    }
  }

  // Fetch all companies
  const fetchAllCompanies = async () => {
    try {
      const response = await fetch(`${apiURl}/api/companies/fetchCompanies`)
      const json = await response.json()
      if (response.status === 200) setCompanies(json.companies.reverse())
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch companies")
    }
  }

  // Fetch company data by ID for editing
  const fetchCompanyDataByID = async id => {
    try {
      const response = await fetch(`${apiURl}/api/companies/fetchCompanyByID`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      })
      const json = await response.json()
      if (response.status === 200 && json.status === "success") {
        const companyData = json.CompanyDataByID[0]
        if (companyData) {
          setFormData({
            companyName: companyData.company_name || "",
            email: companyData.email_ || "",
            phone: companyData.phone_ || "",
            password: "", // Password not retrieved for security
            contactPerson: companyData.contact_person || "",
            businessType: companyData.business_type || "Retailer",
            address: companyData.address || "",
            taxId: companyData.tax_id || "",
            registrationCertificate:
              companyData.registration_certificate || null,
            companyLogo: companyData.company_logo || null,
            website: companyData.website || "",
          })
          setEditCompany(companyData)
          setAccountModal(true)
        }
      }
    } catch (err) {
      console.error("Fetch Company Error:", err)
      toastr.error("Failed to fetch company data")
    }
  }

  // Handle save (create or update)
  const handleSaveCompany = async e => {
    e.preventDefault()

    // Form validation
    if (!formData.companyName.trim())
      return toastr.error("Company Name is required")
    if (!formData.email.trim()) return toastr.error("Email is required")
    if (!formData.phone.trim()) return toastr.error("Phone Number is required")
    if (!formData.password.trim() && !editCompany)
      return toastr.error("Password is required")
    if (!formData.contactPerson.trim())
      return toastr.error("Contact Person Name is required")
    if (!formData.businessType) return toastr.error("Business Type is required")
    if (!formData.address.trim()) return toastr.error("Address is required")
    if (!formData.registrationCertificate && !editCompany)
      return toastr.error("Business Registration Certificate is required")

    const method = editCompany ? "PUT" : "POST"
    const url = editCompany
      ? `${apiURl}/api/companies/edit-company/${editCompany.id}`
      : `${apiURl}/api/companies/add-company`

    const formPayload = new FormData()
    formPayload.append("companyName", formData.companyName)
    formPayload.append("email", formData.email)
    formPayload.append("phone", formData.phone)
    formPayload.append("password", formData.password || "")
    formPayload.append("contactPerson", formData.contactPerson)
    formPayload.append("businessType", formData.businessType)
    formPayload.append("address", formData.address)
    formPayload.append("taxId", formData.taxId)
    if (formData.registrationCertificate) {
      formPayload.append(
        "registrationCertificate",
        formData.registrationCertificate
      )
    }
    if (formData.companyLogo) {
      formPayload.append("companyLogo", formData.companyLogo)
    }
    formPayload.append("website", formData.website)

    try {
      const response = await fetch(url, {
        method,
        body: formPayload,
      })

      const json = await response.json()

      if (response.ok) {
        toastr.success(editCompany ? "Company updated!" : "Company added!")
        fetchAllCompanies()
        setAccountModal(false)
        setEditCompany(null)
        setFormData({
          companyName: "",
          email: "",
          phone: "",
          password: "",
          contactPerson: "",
          businessType: "Retailer",
          address: "",
          taxId: "",
          registrationCertificate: null,
          companyLogo: null,
          website: "",
        })
      } else {
        toastr.error(json.message || "Failed to save company")
      }
    } catch (err) {
      console.error("Save Error:", err)
      toastr.error("Failed to save company")
    }
  }

  // Handle delete
  const deleteCompany = async () => {
    try {
      const response = await fetch(
        `${apiURl}/api/companies/delete-company/${deleteID}`,
        {
          method: "DELETE",
        }
      )
      if (response.ok) {
        toastr.success("Company deleted successfully")
        setDeleteModal(false)
        fetchAllCompanies()
      } else {
        toastr.error("Failed to delete company")
      }
    } catch (err) {
      console.error("Delete Error:", err)
      toastr.error("Failed to delete company")
    }
  }

  // Initialize component
  useEffect(() => {
    fetchAllCompanies()
  }, [])

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Company Name",
        accessor: "company_name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Email",
        accessor: "email_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Contact Person",
        accessor: "contact_person",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Business Type",
        accessor: "business_type",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Address",
        accessor: "address",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Status",
        accessor: "status_",
        Cell: ({ row }) => {
          const isActive = row.original.status_ === "Active"
          const handleToggle = async () => {
            const newStatus = isActive ? "Not Active" : "Active"
            try {
              const res = await fetch(
                `${apiURl}/api/companies/update-status/${row.original.id}`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ status_: newStatus }),
                }
              )
              if (res.ok) {
                toastr.success("Status updated successfully")
                fetchAllCompanies()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }
          return (
            <Form>
              <Form.Check
                type="switch"
                id={`status-switch-${row.original.id}`}
                label={
                  isActive ? (
                    <Badge className="bg-success">Active</Badge>
                  ) : (
                    <Badge className="bg-danger">Inactive</Badge>
                  )
                }
                defaultChecked={isActive}
                onChange={handleToggle}
                style={{
                  color: isActive ? "green" : "red",
                  fontWeight: "bold",
                }}
              />
            </Form>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: cellProps => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              onClick={() => fetchCompanyDataByID(cellProps.value)}
              className="text-primary"
            >
              <i
                className="mdi mdi-eye font-size-18"
                id={`viewtooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${cellProps.value}`}
              >
                View
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => {
                setDeleteID(cellProps.value)
                setDeleteModal(true)
              }}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`deletetooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${cellProps.value}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteCompany}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">Companies</h5>
            </Col>
            <Col>
              <div className="float-end">
                <Button
                  color="primary"
                  className="btn-sm w-md"
                  onClick={() => {
                    setFormData({
                      companyName: "",
                      email: "",
                      phone: "",
                      password: "",
                      contactPerson: "",
                      businessType: "Retailer",
                      address: "",
                      taxId: "",
                      registrationCertificate: null,
                      companyLogo: null,
                      website: "",
                    })
                    setEditCompany(null)
                    setAccountModal(true)
                  }}
                >
                  <i className="fa fa-fw fa-plus me-1" />
                  Create Company
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={companies}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    className="custom-table"
                    customPageSize={10}
                    FilterColumns={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Company Modal */}
      <Modal
        isOpen={accountModal}
        toggle={() => setAccountModal(!accountModal)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSaveCompany}>
          <ModalHeader toggle={() => setAccountModal(!accountModal)}>
            {editCompany ? "Edit Company" : "Create New Company"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Company Name *</Label>
                  <Input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter company name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Password *</Label>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required={!editCompany}
                    />
                    <InputGroupText
                      onClick={togglePasswordVisibility}
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </InputGroupText>
                  </InputGroup>
                </FormGroup>

                <FormGroup>
                  <Label>Contact Person Name *</Label>
                  <Input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder="Enter contact person name"
                    required
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Business Type *</Label>
                  <Input
                    type="select"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Retailer">Retailer</option>
                    <option value="Supplier">Supplier</option>
                    <option value="Contractor">Contractor</option>
                    <option value="Other">Other</option>
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label>Company Address *</Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter company address"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>GST/VAT/Tax ID (Optional)</Label>
                  <Input
                    type="text"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleInputChange}
                    placeholder="Enter GST/VAT/Tax ID"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Business Registration Certificate *</Label>
                  <Input
                    type="file"
                    name="registrationCertificate"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    required={!editCompany}
                  />
                </FormGroup>
                <div className="mb-3">
                  {formData.registrationCertificate && (
                    <img
                      src={
                        typeof formData.registrationCertificate === "string"
                          ? `${apiURl}${formData.registrationCertificate}`
                          : URL.createObjectURL(
                              formData.registrationCertificate
                            )
                      }
                      alt="Certificate Preview"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                        borderRadius: "5px",
                        marginRight: "10px",
                      }}
                    />
                  )}
                </div>

                <FormGroup>
                  <Label>Company Logo (Optional)</Label>
                  <Input
                    type="file"
                    name="companyLogo"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </FormGroup>
                <div className="mb-3">
                  {formData.companyLogo && (
                    <img
                      src={
                        typeof formData.companyLogo === "string"
                          ? `${apiURl}${formData.companyLogo}`
                          : URL.createObjectURL(formData.companyLogo)
                      }
                      alt="Logo Preview"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                        borderRadius: "5px",
                        marginRight: "10px",
                      }}
                    />
                  )}
                </div>

                <FormGroup>
                  <Label>Website URL (Optional)</Label>
                  <Input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="Enter website URL (e.g., https://example.com)"
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setFormData({
                  companyName: "",
                  email: "",
                  phone: "",
                  password: "",
                  contactPerson: "",
                  businessType: "Retailer",
                  address: "",
                  taxId: "",
                  registrationCertificate: null,
                  companyLogo: null,
                  website: "",
                })
                setEditCompany(null)
                setAccountModal(false)
              }}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editCompany ? "Update Company" : "Create Company"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  )
}

export default Companies
